/**
 * User-facing question hints and starter prompts aligned with pod join recipes
 * (assets/hcm-pod-join-recipes.json). Update when new recipes are added.
 */

export const QUESTION_PHRASE_HINTS = {
  bullets: [
    { label: 'Who', text: 'active employees, pending workers, one person number, as-of date' },
    {
      label: 'What columns',
      text: 'job, department, legal employer, business unit, grade, manager, home zip, gender, termination dates, timecard, emergency contacts, salary, element names/amounts, Fusion username, role name, role assignment audit',
    },
    {
      label: 'Users & roles',
      text: 'say "users and role assignments" or "Fusion username and role name" — not job/position roles',
    },
    { label: 'When', text: 'today, as of 2026-05-17, projected hire in the next 30 days, benefits plan year' },
    {
      label: 'Payroll elements',
      text: 'say "element names" vs "element values/amounts"; for US payroll add country US when asked',
    },
    { label: 'Avoid', text: 'table names unless you want a specific pod path' },
  ],
  examples: [
    'Active employees today: person number, name, job, department name, home postal code, and gender.',
    'Headcount: legal employer, business unit, department, job, grade, work location, assignment status — active only, today.',
    'Active employees with payroll element names for US as of today.',
    'Active employees with element values and amounts for US (input name and screen entry value).',
    'Absence plan balance by assignment for PTO plan between start date and as-of date.',
    'Benefits enrollment snapshot as of 2026-01-01: plan type, plan name, coverage dates, person number.',
    'Pending workers with projected start in the next 30 days: manager, location, job, and grade.',
    'Terminated employees (EX_EMP) with last working date and organization.',
    'Emergency contacts with phone and email by employee number.',
    'Web clock clicks for active employees in the last 7 days.',
  ],
};

/** Grouped examples shown under "Query ideas by topic" in SQL Studio. */
export const QUERY_TOPIC_HINTS = [
  {
    id: 'headcount',
    label: 'Headcount & demographics',
    recipeIds: ['employee-demographics-core'],
    questions: [
      'Active employees today: person number, name, job, department, home postal code, gender.',
      'List active primary employees with legal employer, business unit, department, job, grade, and work location.',
    ],
  },
  {
    id: 'org',
    label: 'Org (department / BU / legal employer / PSU / enterprise)',
    recipeIds: [
      'employee-org-details-by-class-v',
      'employee-department-classification',
      'employee-business-unit-fun-business-unit',
      'employee-legal-employer-hcm-lemp',
    ],
    questions: [
      'Person number and department name for all active primary employees today.',
      'Show business unit name and legal employer name by person number.',
      'Active employees: department, business unit, legal employer, payroll statutory unit, and enterprise (HR_ORG_DETAILS_BY_CLASS_V).',
    ],
  },
  {
    id: 'grade',
    label: 'Grade',
    recipeIds: ['employee-grade-name-assignment'],
    questions: ['Active employees with grade name as of today.', 'List person number, name, and pay grade for active workers.'],
  },
  {
    id: 'payroll-elements',
    label: 'Payroll elements',
    recipeIds: [
      'payroll-element-entries-active',
      'payroll-element-entry-values-active',
      'legislative-data-groups-by-country',
    ],
    questions: [
      'Active employees with payroll element names for US as of today.',
      'Active employees with element values and amounts for US: element name, input name, screen entry value.',
      'Which legislative data groups are configured? (country codes)',
    ],
  },
  {
    id: 'absence',
    label: 'Absence & accrual',
    recipeIds: ['absence-plan-balance', 'absence-entry-audit', 'hcm-us-absence-entries'],
    questions: [
      'Absence plan balance by assignment number for active employees.',
      'Absence entry audit: colleague name, absence type, start/end, who created and last updated the entry.',
      'US absence entries: person number, full name, assignment, absence type, start/end, duration, approval status (ANC_PER_ABS_ENTRIES).',
      'US absence entries from DATE \'2026-01-01\' to DATE \'2026-02-28\' (overlap window — WHERE + date filter applied automatically).',
      'All US legislative data group absence rows in the last 12 months or open-ended absences (default when no custom dates).',
    ],
  },
  {
    id: 'benefits',
    label: 'Benefits enrollment',
    recipeIds: [
      'benefits-enrollment-report',
      'benefits-dependent-coverage',
      'benefits-life-events-potential',
    ],
    questions: [
      'Benefits enrollments as of 2026-01-01: person number, plan type, plan name, option, coverage start and end.',
      'Dependent benefit coverage: employee, dependent name, relationship, coverage dates, and amount.',
      'Benefits life events with status, occurred date, and processed date.',
    ],
  },
  {
    id: 'termination-rehire',
    label: 'Termination & rehire',
    recipeIds: ['employee-termination-ex-emp', 'employee-rehire'],
    questions: [
      'Terminated employees with last working date, actual termination date, and organization.',
      'Rehires with rehire date, reason, and prior termination on file.',
    ],
  },
  {
    id: 'time',
    label: 'Time & attendance',
    recipeIds: ['hxt-timecard-matrix', 'hxt-web-clock-clicks'],
    questions: [
      'Timecard rows: person number, timecard start/end, block type, and organization.',
      'Web clock punches: person name, clicked time, and organization.',
    ],
  },
  {
    id: 'contacts',
    label: 'Contacts (relationships / email / emergency)',
    recipeIds: [
      'employee-contacts-all',
      'employee-contact-directory',
      'employee-emergency-contacts',
    ],
    questions: [
      'All contact relationships: employee name, contact person name, contact type, beneficiary and dependent flags.',
      'Employee work email, personal email, work phone, and mobile phone by person number.',
      'Emergency contacts with contact name, phone, and email by employee number.',
    ],
  },
  {
    id: 'contacts-salary',
    label: 'Address & salary',
    recipeIds: [
      'employee-mailing-address',
      'employee-supervisor-expense-account',
      'employee-salary-cmp',
    ],
    questions: [
      'Mailing address by person number with organization.',
      'Active employees: manager name, work email, and GL expense account segments.',
      'Current salary amount and currency by person number with job and location.',
    ],
  },
  {
    id: 'assignment-detail',
    label: 'Job / position / grade / absence case',
    recipeIds: ['employee-active-job-position-grade', 'employee-absence-cases'],
    questions: [
      'Active employees with job name, position name, grade, and hire date.',
      'Active employees with an absence case name and assignment number.',
    ],
  },
  {
    id: 'pending',
    label: 'Pending workers',
    recipeIds: ['pending-worker-report'],
    questions: [
      'Pending workers with projected start in the next 30 days: name, hire date, manager, location, job, grade.',
    ],
  },
  {
    id: 'hdl-load-errors',
    label: 'HCM Data Loader (HDL) errors',
    recipeIds: ['hcm-hdl-load-errors', 'hcm-hdl-load-errors-all', 'hcm-hdl-dat-line-errors'],
    questions: [
      'List all HDL load errors from the last 30 days with data set name, .dat file name, error message, and line user key.',
      'Show HCM Data Loader failures for a specific data set or request id, newest errors first.',
      'What validation or import errors occurred on my last Worker.dat or Assignment.dat load?',
      'Show all HDL message lines from the last 30 days including warnings and info (no message type filter).',
      'Show failed .dat file lines with UI_USER_KEY, line status, and dat line text (HRC_DL_LOGICAL_LINES + FILE_LINES).',
      'List HDL error records from the .dat file — logical line errors, not message lines only.',
    ],
  },
];

/** User Lookup — roles / functional / data security swimlane (not shown in SQL Studio topic list). */
export const SECURITY_ACCESS_MAP_TOPIC = {
  id: 'security-users-roles',
  label: 'Users & security access map (roles / privileges / person scope)',
  recipeIds: ['hcm-user-security-access-map', 'hcm-user-role-assignments'],
  questions: [
    'User access map for JDOE: roles, duty-role privileges, data security, and secured persons.',
    'Which other employees can this Fusion user see — person security profile and delegations?',
    'Show duty roles with role common name and what data scope a user has from job roles.',
  ],
};

/** One-click starters for Oracle SQL mode (pod-tested paths preferred). */
export const ORACLE_SQL_STARTERS = [
  'Active employees today: person number, name, job, department, home postal code, and gender.',
  'Active employees with payroll element names for US as of today.',
  'Active employees with element values and amounts for US.',
  'Person number and department name for active primary employees.',
  'Department, business unit, legal employer, payroll statutory unit, and enterprise by person number.',
  'Absence plan balance by assignment for active employees.',
  'US absence entries from DATE \'2026-01-01\' to DATE \'2026-02-28\': person, assignment, absence type, start/end, approval status.',
  'Benefits enrollments as of today: plan type, plan name, coverage dates.',
  'Pending workers starting in the next 30 days with manager and job.',
  'Terminated employees with last working date and organization.',
  'All contact relationships with contact type and beneficiary or dependent flags.',
  'Emergency contacts with phone and email by employee number.',
  'Current salary by person number with job and location.',
  'Web clock clicks for employees today.',
  'Dependent benefit coverage with relationship and coverage dates.',
  'Fusion users and role assignments: username, active flag, role name, audit dates.',
  'HCM Data Loader errors in the last 30 days: file name, data set status, error message, line user key.',
];
